/**
 * Client game store: the fog-limited `PlayerView` (protocol reducer) plus what only the
 * client needs — per-hex render state, capture-glow times, first-seen ticks of tracks,
 * long-lived ash scars, a small event stream for alerts and sound, and the per-frame
 * interaction state the renderer draws in the world (`aim`, `placement`, `inboundThreats`).
 */
import type { LoadedWorld } from '@sa/mapdata';
import { activeAllies, applyMatchEnd, applySnapshot, createPlayerView, eliminatedFromB, isFriendlyTo, type EndMessage, type NetSnapshot, type ViewInit } from '@sa/protocol';
import { BALANCE, axialDist, segPos, type BuildKind, type Detonation, type EntityId, type EntityKind, type EntityView, type HexIdx, type LaunchHint, type MissileTrack, type PlayerId, type PlayerView, type Tick } from '@sa/sim';
import { HEX_FOCUS, HEX_LIVE, HEX_SEEN, HEX_STALE_MAX, HEX_STALE_SHIFT, STALE_STEP_TICKS, STATE_W } from '../render/hexfield';
import type { StrikeOffer } from '../input/commands';
import { blastRingMu, nearestLandHex } from './geo';
import { DiplomacyBook } from './diplomacy';
import { nationPlaces, type NationPlaces } from './nations';
import { ThreatTracker, blastStake, friendlyCapitals, placeLabel, type InboundThreat, type ViewRect } from './threats';

export { nearestLandHex } from './geo';
export type { InboundThreat, ViewRect } from './threats';

/** What the cursor is over while aiming a launch. */
export type AimZone = 'own' | 'ally' | 'enemy' | 'neutral' | 'water';

/**
 * Launch aim (null unless the player is aiming a silo / sub launch). Set every frame by the
 * session from the input controller; the renderer draws reticle, blast rings and flight time.
 */
export interface AimState {
  /** cursor in world units (mu) */
  x: number;
  y: number;
  /** primary launcher (first ready one, else the first selected) */
  launcherId: EntityId;
  kind: 'icbm' | 'slbm';
  /** whose land the cursor is on (0: neutral land or the sea) — named in the caption */
  owner: PlayerId;
  /** the target is on our or an ally's land (friendly fire / blocked) */
  ownOrAllied: boolean;
  zone: AimZone;
  /** the target hex is foreign land outside current sensor cover (draw dim with "?") */
  fog: boolean;
  /** an ally's land: the server refuses, clicks do nothing (⊘ "ALLY · BLOCKED") */
  blocked: boolean;
  /** own land: the warning click was made, the next click (or Shift+click) fires */
  confirmArmed: boolean;
  /** launchers that fire on click */
  ready: number;
  /** missiles loaded in the selected launchers that reach the target (what a ripple fires) */
  loaded: number;
  /** ripple mode (Q pressed again): the click empties the magazines, one per launcher as each is ready */
  ripple: boolean;
  /** what the click fires, e.g. "ICBM ×2", "SLBM", "RIPPLE ×8" */
  salvo: string;
  /** why a click would not fire, e.g. "NO MISSILE READY", "OUT OF RANGE" (null: it fires) */
  reason: string | null;
  /** outer radius of the whole blast footprint (ring 2), mu */
  blastRadiusMu: number;
  /** outer radii of rings 0, 1, 2 (mu): structures die in 0–1 (silos 0), cities lose levels */
  blastRingsMu: readonly [number, number, number];
  /** flight time from the primary launcher to the cursor, seconds */
  flightSeconds: number;
}

/** Build placement under the cursor (null unless placing). */
export interface PlacementState {
  kind: BuildKind;
  /** snapped to the hex centre when over land (mu) */
  x: number;
  y: number;
  valid: boolean;
  /** why it cannot be placed here ("OWN LAND ONLY", "TOO CLOSE TO A CITY", …), null if valid */
  reason: string | null;
  /** caption for the cursor, e.g. "SILO · 2,400" */
  label: string;
  /** range ring to draw (mu), 0 = none */
  range: number;
}

/** What shot an aircraft down, in words. */
export type ShooterWord = 'SAM' | 'CARRIER SAM' | 'FIGHTER';

/** Things worth a sound or an alert line; produced by `apply`. */
export type GameEvent =
  /** a new missile track; `alarm`: hostile and about to hit something of ours (never open ground, an ally's or a spectator's) */
  | { type: 'track'; id: EntityId; alarm: boolean }
  | { type: 'hint' }
  | { type: 'detonation'; x: number; y: number; exact: boolean; mine: boolean }
  | { type: 'intercept'; hit: boolean; targetId: EntityId }
  | { type: 'phase'; kind: 'release' | 'final' | 'beacon' | 'capitalsExposed' }
  /**
   * A player is out. `decapitated`: their capital fell (§1.11); `by`: who took or struck it,
   * as far as we saw (the capital hex's new owner, else the last warhead we tracked onto it), 0 unknown.
   */
  | { type: 'eliminated'; player: PlayerId; decapitated: boolean; by: PlayerId; tick: Tick }
  /** our ally and we parted because one of us fell (`us`: we did): the ALLY chat line stays */
  | { type: 'allyFell'; ally: PlayerId; us: boolean }
  /** an alliance formed or ended; `theyBroke`: the other side broke it (an ally knocked out is no break) */
  | { type: 'ally'; with: PlayerId; on: boolean; theyBroke?: boolean }
  /** two other nations: `breaker` broke its alliance with `victim` (public; a knock-out's dissolution is none) */
  | { type: 'betrayal'; breaker: PlayerId; victim: PlayerId }
  /** a nation turned down our alliance offer */
  | { type: 'allyDeclined'; by: PlayerId }
  /**
   * A ceasefire of ours began (`on`) or ended: `by` broke it early (us or them), `ended` it ran its
   * course. Another pair's broken ceasefire is `ceasefireBroken` (breaking one is announced to everyone).
   */
  | { type: 'ceasefire'; with: PlayerId; on: boolean; by?: PlayerId; ended?: boolean }
  | { type: 'ceasefireOffer'; from: PlayerId }
  | { type: 'ceasefireDeclined'; by: PlayerId }
  | { type: 'ceasefireBroken'; a: PlayerId; b: PlayerId }
  /** another nation sent us credits */
  | { type: 'gift'; from: PlayerId; amount: number }
  /** our side marked a target (`from`: us or our ally / a teammate) */
  | { type: 'mark'; from: PlayerId; target: PlayerId }
  /** a blast hit one of our braced cities: it kept its levels */
  | { type: 'held'; name: string | null }
  /** our fighters took off after a hostile bomber (auto-scramble) */
  | { type: 'scramble'; fighters: number }
  /**
   * A capital went critical (`on`, §1.11 second life) or recovered. `fresh`: it just went critical
   * (a later hit restarting the window is not fresh).
   */
  | { type: 'critical'; player: PlayerId; on: boolean; fresh: boolean }
  | { type: 'allyPrompt'; from: PlayerId; expiresTick: Tick }
  | { type: 'hexLost' }
  /**
   * One of our structures / ships was destroyed (`name` for named cities); an aircraft of ours shot
   * down carries what did it (`SAM`, `CARRIER SAM`, `FIGHTER`) and whose, when we saw it (0: unseen)
   */
  | { type: 'lost'; kind: EntityKind; name: string | null; by?: { what: ShooterWord; owner: PlayerId } }
  /** our SAMs / carrier / fighters shot down a rival's aircraft */
  | { type: 'airKill'; kind: 'fighter' | 'bomber'; owner: PlayerId; by: ShooterWord }
  /** one of our cities lost levels to a blast */
  | { type: 'cityHit'; name: string | null; levels: number }
  /**
   * Every missile of one of our strikes (launched at one target) is down: how many got through
   * (`through`), how many were shot down, and whether we saw the impacts (`seen`: in our sensors).
   */
  | { type: 'strike'; label: string; n: number; through: number; seen: boolean; owner: PlayerId; wasted?: number }
  | { type: 'end' };

/** One of our units as it was before a snapshot (what a loss line needs). */
interface OwnRow {
  kind: EntityKind;
  level: number;
  x: number;
  y: number;
  /** an aircraft in the air */
  flying: boolean;
  /** a fighter chasing a hostile bomber (auto-scramble) */
  intercepting: boolean;
}

/** Ash scars outlive the reducer's 10 s detonation memory. */
export const SCAR_TICKS = 1800;
const FOCUS_RADIUS = 5;
const GONE_TRACK_TICKS = 100;
/** after T (SAM focus) the engaging SAMs' lines to the missile flash this long (ms) */
export const SAM_FOCUS_FLASH_MS = 1200;
/** a track launched this close in time (ticks) to a launch hint is that launch */
const HINT_TRACK_TICKS = 3;
/** our own impacts leave a flash pip this long (ticks), seen or not */
export const IMPACT_PIP_TICKS = 100;
/** a mark (a target our side pointed out) stays on its capital this long (ticks) */
export const MARK_TICKS = 300;

/** One of our strikes: the missiles we launched at one target, until the last of them is down. */
interface Strike {
  label: string;
  /** whose land it was aimed at, at launch (public borders; 0 neutral / sea) */
  owner: PlayerId;
  x: number;
  y: number;
  ids: EntityId[];
  through: number;
  down: number;
  seen: boolean;
  /** of `through`: landed after `owner` was already out (wasted, as the results count them) */
  wasted: number;
}

/** Two of our warheads aimed this close (hex radii × √3) belong to one strike. */
const STRIKE_SAME_HEXES = 1.5;

/** a shot-down aircraft's last spot is kept this long (ticks) for its burst */
const AIR_MEMORY_TICKS = 60;

/** nation name anchors are recomputed this often (ticks) */
const NATION_PLACES_TICKS = 20;

export class GameStore {
  readonly view: PlayerView;
  /** RGBA8UI per hex: owner, dens, rad, flags (STATE_W-wide texture layout) */
  readonly hexState: Uint8Array;
  /** client seconds of the last visible owner change per hex */
  readonly flipTime: Float32Array;
  hexDirty = true;
  flipDirty = true;
  /** tick each missile track was first received (trail starts there) */
  readonly trackSeen = new Map<EntityId, Tick>();
  scars: Detonation[] = [];
  /** every exact detonation of the match (the final world behind the results keeps them all) */
  private allScars: Detonation[] = [];
  /** the match is over: draw every known hex as seen-now (the fog lifts behind the results) */
  private revealed = false;
  /** every player knocked out so far and when (phase events expire from the view; this does not) */
  private out = new Map<PlayerId, Tick>();
  /** tracks that left the view recently (intercept bursts need their last path) */
  readonly goneTracks = new Map<EntityId, { track: MissileTrack; tick: Tick }>();
  private focusHex: number | null = null;
  private focusZone: Uint8Array;
  private listeners: ((e: GameEvent) => void)[] = [];
  private threats = new ThreatTracker();
  private capitals = new Set<HexIdx>();
  /** when each critical capital went critical (its grace runs from there) */
  private criticalSince = new Map<PlayerId, Tick>();
  /** every named place (map cities, then match city names on top) for threat bearings */
  private refs: Map<HexIdx, string>;
  started = false;
  /** every alliance of the match (they are public), who may not ally yet, our dealings (`state/diplomacy.ts`) */
  readonly diplomacy = new DiplomacyBook();
  /** targets our side marked lately (one per nation, the latest): drawn on their capital */
  marks: { from: PlayerId; target: PlayerId; tick: Tick }[] = [];

  /**
   * Hostile missiles that land on our own ground (or would hit something of ours), most urgent
   * first. Recomputed every frame by `updateFrame` (ETA, head position and on-screen flags move
   * continuously). Red, alarms, brace and SAM focus are about these only.
   */
  inboundThreats: InboundThreat[] = [];
  /** hostile missiles that only hit an ally: shown, calmly (teal), never as our alarm */
  allyThreats: InboundThreat[] = [];
  /** where the focused front's caption sits (mu): just outside the middle of the pushed edge, and which way is out */
  frontAnchor: { x: number; y: number; dx: number; dy: number } | null = null;
  /** launch aim under the cursor; null unless aiming a launch (see `AimState`) */
  aim: AimState | null = null;
  /** build placement under the cursor; null unless placing */
  placement: PlacementState | null = null;
  /** A nation's callsign as the HUD writes it (`KESTREL`), or `P3`. */
  nameOf(p: PlayerId): string {
    return (this.view.players.find((q) => q.id === p)?.name ?? `P${p}`).toUpperCase();
  }

  /** a first-match cue next to the cursor (mu), e.g. "RMB · PUSH" over land we could push into */
  cursorCue: { x: number; y: number; text: string } | null = null;
  /** a nation the Tab board's pointer is on: its land pulses on the map (since `atMs`) */
  highlight: { player: PlayerId; atMs: number } | null = null;
  /**
   * A nation that just broke its alliance (or a ceasefire, `small`) with us: its land pulses red for
   * a few seconds (and, for an alliance, the screen glitches once). Since `atMs`.
   */
  betrayalPulse: { player: PlayerId; atMs: number; small: boolean } | null = null;
  /** screen rects (css px) of HUD blocks the world's words keep out of (toasts, chip, nation menu), set by the session */
  hudBlocks: { x: number; y: number; w: number; h: number }[] = [];
  private places: NationPlaces | null = null;
  /** aircraft we have seen lately, where (mu) and whose: a shot at one is drawn there even after it fell */
  private air = new Map<EntityId, { x: number; y: number; kind: 'fighter' | 'bomber'; owner: PlayerId; tick: Tick }>();
  private placesTick = -Infinity;
  /**
   * Launch hints still worth showing: a launch we track is not a
   * "LAUNCH DETECTED · NO TRACK" — the track says it all. Recomputed on every snapshot.
   */
  hints: LaunchHint[] = [];
  private trackedHints = new Set<number>();
  /** G pressed more than 20 s before the impact: cities whose brace is armed (the session sends each in time) */
  readonly braceArms = new Set<EntityId>();
  /** a rival's critical capital our launchers reach (Q STRIKE), refreshed by the session */
  strikeOffer: StrikeOffer | null = null;
  /** where our own warheads landed (the true aim point) and when: a flash pip, even in the fog */
  impacts: { x: number; y: number; tick: Tick }[] = [];
  /** our strikes in flight (for the outcome line) */
  private strikes: Strike[] = [];
  /** the last T (SAM focus): which threat, the SAMs told to engage it, when (performance.now ms) */
  samFocus: { threatId: EntityId; sams: EntityId[]; atMs: number } | null = null;

  constructor(
    readonly world: LoadedWorld,
    init: ViewInit,
    /** match-start city names by hex (`cityNames`) */
    readonly names: ReadonlyMap<HexIdx, string> = new Map(),
  ) {
    this.view = createPlayerView(init);
    this.refs = new Map([...world.cities.map((c) => [c.hex, c.name] as const), ...names]);
    const n = Math.ceil(world.landCount / STATE_W) * STATE_W;
    this.hexState = new Uint8Array(n * 4);
    this.flipTime = new Float32Array(n).fill(-1e6);
    this.focusZone = new Uint8Array(world.landCount);
  }

  on(fn: (e: GameEvent) => void): () => void {
    this.listeners.push(fn);
    return () => (this.listeners = this.listeners.filter((f) => f !== fn));
  }

  private emit(e: GameEvent): void {
    for (const f of this.listeners) f(e);
  }

  get me(): PlayerId {
    return this.view.me;
  }

  /** Our start region's capital hex (−1 if unknown). */
  get capitalHex(): number {
    return this.capitalOf(this.view.me);
  }

  /** A player's capital hex (their start region's capital; public knowledge), −1 if unknown. */
  capitalOf(p: PlayerId): number {
    const info = this.view.players.find((q) => q.id === p);
    return this.world.regions.find((r) => r.id === info?.regionId)?.capitalHex ?? -1;
  }

  /** the ally of when one of us fell (FFA): the ALLY chat line both keep (0: none) */
  fallenAlly: PlayerId = 0;

  /** Players knocked out so far, with the tick they fell. */
  get eliminated(): ReadonlyMap<PlayerId, Tick> {
    return this.out;
  }

  /** we broke an alliance: no new one until this tick (§2 betrayal lock; 0 = none) */
  get allyCooldownUntil(): Tick {
    return this.diplomacy.cooldownUntil(this.view.me);
  }

  /** Allies that still bind us (none for a spectator): the one list every part of the client reads. */
  get allies(): readonly PlayerId[] {
    return activeAllies(this.view).filter((p) => !this.out.has(p));
  }

  isFriendly(p: PlayerId): boolean {
    return p === this.view.me ? p !== 0 : isFriendlyTo(this.view, p) && !this.out.has(p);
  }

  /** the (fractional) display tick of the frame being drawn (animations between snapshots) */
  frameTick = 0;

  /** Per-frame refresh of `inboundThreats` at the (fractional) display tick. */
  updateFrame(tick: number, visible: ViewRect | null): void {
    this.frameTick = tick;
    // a spectator has nothing of their own to lose: every track is just a track
    if (this.view.eliminated) {
      this.inboundThreats = [];
      this.allyThreats = [];
      return;
    }
    const ctx = { world: this.world, names: this.names, refs: this.refs, capitals: this.capitals, criticalSince: this.criticalSince };
    const all = this.threats.compute(this.view, ctx, tick, visible);
    this.inboundThreats = all.filter((t) => !t.ally);
    this.allyThreats = all.filter((t) => t.ally);
  }

  /** Apply one decoded snapshot; `nowS` is the client clock (seconds) for capture glow. */
  apply(s: NetSnapshot, nowS: number): void {
    const v = this.view;
    const prevOwner = s.full ? null : v.owner.slice();
    const prevAllies = this.allies.slice();
    const hadTrack = new Set(v.tracks.keys());
    const prevTracks = new Map(v.tracks);
    const prevOwn = s.full ? null : this.ownSnapshot();
    const wasCritical = new Set(v.criticalCapitals.keys());
    const prevCeasefires = [...v.ceasefires.keys()];
    applySnapshot(v, s);
    this.noteAircraft();
    this.diplomacy.apply(s.phase, s.full);
    this.updateCriticalSince(s, wasCritical);
    for (const ph of s.phase) if (ph.kind === 'eliminated' && !this.out.has(ph.a)) this.out.set(ph.a, ph.tick);
    // an alliance that ended with a fall — theirs or ours, by surrender or a lost capital — leaves
    // the two an ALLY line (FFA; the server keeps it too): remembered from the moment it happens
    if (v.mode !== 'teams')
      for (const p of prevAllies) {
        if (this.allies.includes(p) || !(this.out.has(p) || v.eliminated) || this.fallenAlly === p) continue;
        this.fallenAlly = p;
        this.emit({ type: 'allyFell', ally: p, us: v.eliminated });
      }
    this.started = true;
    this.capitals = friendlyCapitals(v, this.world);

    if (prevOwner) {
      for (const o of s.owners) {
        if (prevOwner[o.hex] === o.owner) continue;
        this.flipTime[o.hex] = nowS;
        if (prevOwner[o.hex] === v.me) this.emit({ type: 'hexLost' });
      }
      if (s.owners.length) this.flipDirty = true;
    }

    for (const t of s.tracks) {
      if (hadTrack.has(t.id)) continue;
      this.trackSeen.set(t.id, v.tick);
      this.noteFire(t);
      if (!s.full && t.owner === v.me) this.addToStrike(t);
      if (!s.full) this.emit({ type: 'track', id: t.id, alarm: !v.eliminated && !this.isFriendly(t.owner) && this.targetsOwn(t.tx, t.ty) && this.hitsOwn(t.tx, t.ty) });
    }
    const hintsBefore = this.hints.length;
    this.updateHints();
    for (const id of hadTrack) {
      if (v.tracks.has(id)) continue;
      const t = prevTracks.get(id);
      if (t) {
        this.goneTracks.set(id, { track: t, tick: v.tick });
        if (t.owner === v.me) this.resolveStrike(t, !s.removes.some((r) => r.id === id && r.reason === 'intercepted'), s.detonations);
      }
      this.trackSeen.delete(id);
    }
    for (const [id, g] of this.goneTracks) if (v.tick - g.tick > GONE_TRACK_TICKS) this.goneTracks.delete(id);
    if (this.impacts.length && v.tick - this.impacts[0].tick > IMPACT_PIP_TICKS) this.impacts = this.impacts.filter((p) => v.tick - p.tick <= IMPACT_PIP_TICKS);

    if (!s.full) {
      if (s.hints.length && this.hints.length > hintsBefore) this.emit({ type: 'hint' });
      for (const d of s.detonations) {
        this.emit({ type: 'detonation', x: d.x, y: d.y, exact: d.exact, mine: !v.eliminated && this.targetsOwn(d.x, d.y) });
        // a brace that paid off: the city took the blast and kept its levels
        const held = v.eliminated ? undefined : this.bracedCityNear(d.x, d.y);
        if (held) this.emit({ type: 'held', name: this.cityNameAt(held.x, held.y) });
      }
      for (const i of s.intercepts) if (i.hit !== undefined) this.emit({ type: 'intercept', hit: i.hit, targetId: i.targetId });
      const brokeUs = new Set<PlayerId>();
      // (an alliance dissolved because one side was knocked out is no break: `out` already has it)
      /** ceasefires of ours broken in this snapshot: the other nation → the breaker */
      const brokeCeasefire = new Map<PlayerId, PlayerId>();
      for (const ph of s.phase) {
        if (ph.kind === 'release' || ph.kind === 'final' || ph.kind === 'beacon' || ph.kind === 'capitalsExposed') this.emit({ type: 'phase', kind: ph.kind });
        // (who did it: the server's word, the same the knock-out panel and the results tell)
        else if (ph.kind === 'eliminated') this.emit({ type: 'eliminated', player: ph.a, ...eliminatedFromB(ph.b), tick: ph.tick });
        else if (ph.kind === 'capitalCritical') this.emit({ type: 'critical', player: ph.a, on: true, fresh: !wasCritical.has(ph.a) });
        else if (ph.kind === 'capitalRecovered' && wasCritical.has(ph.a)) this.emit({ type: 'critical', player: ph.a, on: false, fresh: false });
        else if (ph.kind === 'allyDeclined' && ph.a === v.me) this.emit({ type: 'allyDeclined', by: ph.b });
        else if (ph.kind === 'allyBroken' && !this.out.has(ph.a) && !this.out.has(ph.b)) {
          // a betrayal, remembered for the rest of the match (Tab, the nation menu)
          this.diplomacy.noteBetrayal(ph.a, ph.b, ph.tick);
          if (ph.b === v.me) brokeUs.add(ph.a);
          else if (ph.a !== v.me && !v.eliminated) this.emit({ type: 'betrayal', breaker: ph.a, victim: ph.b });
        }
        else this.diplomacyEvent(ph, brokeCeasefire);
      }
      this.ceasefireChanges(prevCeasefires, brokeCeasefire, nowS);
      for (const r of s.allyPrompts) if (r.to === v.me) this.emit({ type: 'allyPrompt', from: r.from, expiresTick: r.expiresTick });
      const allies = this.allies;
      for (const p of allies) if (!prevAllies.includes(p)) this.emit({ type: 'ally', with: p, on: true });
      // an ally knocked out (or our own knock-out) has its own notice, not "ALLIANCE BROKEN"
      for (const p of prevAllies) {
        if (allies.includes(p) || this.out.has(p) || v.eliminated) continue;
        if (brokeUs.has(p)) this.betrayalPulse = { player: p, atMs: nowS * 1000, small: false };
        this.emit({ type: 'ally', with: p, on: false, theyBroke: brokeUs.has(p) });
      }
    }
    if (prevOwn) this.emitLosses(prevOwn, s);
    for (const d of s.detonations) {
      this.scars.push(d);
      if (d.exact) this.allScars.push(d);
    }
    if (this.scars.length && v.tick - this.scars[0].tick > SCAR_TICKS) this.scars = this.scars.filter((d) => v.tick - d.tick <= SCAR_TICKS);

    const refocus = v.focus !== this.focusHex;
    if (refocus) this.recomputeFocus(v.focus);
    if (refocus || s.full || s.owners.length) this.frontAnchor = this.computeFrontAnchor();
    this.rebuildHexState();
  }

  /** Ceasefire offers, declines, other pairs' breaks, gifts and marks: alert lines and map marks. */
  private diplomacyEvent(ph: NetSnapshot['phase'][number], brokeCeasefire: Map<PlayerId, PlayerId>): void {
    const me = this.view.me;
    switch (ph.kind) {
      case 'ceasefireOffer':
        if (ph.b === me) this.emit({ type: 'ceasefireOffer', from: ph.a });
        break;
      case 'ceasefireDeclined':
        if (ph.a === me) this.emit({ type: 'ceasefireDeclined', by: ph.b });
        break;
      case 'ceasefireBroken':
        if (ph.a === me || ph.b === me) brokeCeasefire.set(ph.a === me ? ph.b : ph.a, ph.a);
        else if (!this.view.eliminated) this.emit({ type: 'ceasefireBroken', a: ph.a, b: ph.b });
        break;
      case 'gift':
        this.emit({ type: 'gift', from: ph.a, amount: ph.b * 100 });
        break;
      case 'mark':
        this.marks = [...this.marks.filter((m) => m.target !== ph.b), { from: ph.a, target: ph.b, tick: ph.tick }];
        if (ph.a === me) this.diplomacy.markedAt = ph.tick;
        this.emit({ type: 'mark', from: ph.a, target: ph.b });
        break;
    }
  }

  /** Our ceasefires that began or ended in this snapshot (a break, or the clock running out). */
  private ceasefireChanges(prev: readonly PlayerId[], broke: ReadonlyMap<PlayerId, PlayerId>, nowS: number): void {
    const now = this.view.ceasefires;
    for (const p of now.keys()) if (!prev.includes(p)) this.emit({ type: 'ceasefire', with: p, on: true });
    for (const p of prev) {
      if (now.has(p)) continue;
      const by = broke.get(p);
      if (by !== undefined) {
        // they broke it on us: their land pulses red a moment (smaller than an alliance's betrayal)
        if (by !== this.view.me) this.betrayalPulse = { player: p, atMs: nowS * 1000, small: true };
        this.emit({ type: 'ceasefire', with: p, on: false, by });
      }
      // (an alliance or a knock-out replaced it: those have their own lines)
      else if (!this.allies.includes(p) && !this.out.has(p) && !this.view.eliminated) this.emit({ type: 'ceasefire', with: p, on: false, ended: true });
    }
  }

  /** A new track is fire exchanged: theirs onto our land, or ours onto someone's (AT WAR in the nation menu). */
  private noteFire(t: MissileTrack): void {
    const h = nearestLandHex(this.world, t.tx, t.ty);
    const o = h >= 0 ? this.view.owner[h] : 0;
    if (t.owner === this.view.me) {
      if (o !== this.view.me) this.diplomacy.noteFire(o, this.view.tick);
    } else if (o === this.view.me) this.diplomacy.noteFire(t.owner, this.view.tick);
  }

  /** Marks still worth drawing (the latest per nation, for MARK_TICKS). */
  liveMarks(tick: number): { from: PlayerId; target: PlayerId; tick: Tick }[] {
    if (this.marks.length && this.marks.some((m) => tick - m.tick > MARK_TICKS)) this.marks = this.marks.filter((m) => tick - m.tick <= MARK_TICKS);
    return this.marks.filter((m) => !this.out.has(m.target));
  }

  end(msg: EndMessage): void {
    applyMatchEnd(this.view, msg);
    // the final world: every scar of the match, every known hex drawn as seen
    this.revealed = true;
    this.scars = this.allScars;
    this.rebuildHexState();
    this.emit({ type: 'end' });
  }

  /**
   * Where each nation's name goes on the map (`state/nations.ts`): recomputed from the public
   * borders every 2 s, steady in between (and across small border changes).
   */
  nationPlaces(tick: number): NationPlaces {
    if (!this.places || Math.abs(tick - this.placesTick) >= NATION_PLACES_TICKS) {
      this.places = nationPlaces(this.world, this.view.owner, this.places?.anchors);
      this.placesTick = tick;
    }
    return this.places;
  }

  /** Our capital city (finished, still ours), if we hold it. */
  ownCapitalCity(): EntityView | undefined {
    const h = this.capitalHex;
    if (h < 0) return undefined;
    for (const e of this.view.entities.values()) {
      if (e.kind === 'city' && e.owner === this.view.me && e.state !== 'building' && nearestLandHex(this.world, e.x, e.y) === h) return e;
    }
    return undefined;
  }

  /** Ticks left on `p`'s critical capital window at `tick`, or null when it is not critical. */
  criticalLeft(p: PlayerId, tick: number): number | null {
    const until = this.view.criticalCapitals.get(p);
    return until === undefined ? null : Math.max(0, until - tick);
  }

  /** Track when each capital went critical: a window that is new in this snapshot starts at its event. */
  private updateCriticalSince(s: NetSnapshot, was: ReadonlySet<PlayerId>): void {
    const crit = this.view.criticalCapitals;
    for (const p of [...this.criticalSince.keys()]) if (!crit.has(p)) this.criticalSince.delete(p);
    for (const ph of s.phase) {
      if (ph.kind === 'capitalCritical' && crit.has(ph.a) && (s.full || !was.has(ph.a))) this.criticalSince.set(ph.a, ph.tick);
    }
  }

  /** Where a blast at (x, y) is, for people: "KAZAN", "NEAR TIMBUKTU" or "120 KM N OF PARIS". */
  placeName(x: number, y: number): string {
    return placeLabel(this.world, this.names, x, y, this.refs);
  }

  /** A missile of ours appeared: it joins the strike on the same target (a salvo, a ripple) or starts one. */
  private addToStrike(t: MissileTrack): void {
    const same = STRIKE_SAME_HEXES * Math.sqrt(3) * this.world.hexR;
    const k = this.strikes.find((k) => (k.x - t.tx) ** 2 + (k.y - t.ty) ** 2 <= same * same);
    if (k) k.ids.push(t.id);
    else {
      const hex = nearestLandHex(this.world, t.tx, t.ty);
      const owner = hex >= 0 && (this.world.cx[hex] - t.tx) ** 2 + (this.world.cy[hex] - t.ty) ** 2 <= this.world.hexR ** 2 * 1.5 ? this.view.owner[hex] : 0;
      this.strikes.push({ label: this.placeName(t.tx, t.ty), owner, x: t.tx, y: t.ty, ids: [t.id], through: 0, down: 0, seen: false, wasted: 0 });
    }
  }

  /** A missile of ours is gone (`through`: it detonated, else it was shot down); the strike's outcome once all are. */
  private resolveStrike(t: MissileTrack, through: boolean, dets: readonly Detonation[]): void {
    const k = this.strikes.find((k) => k.ids.includes(t.id));
    if (through) this.impacts.push({ x: t.tx, y: t.ty, tick: this.view.tick });
    if (!k) return;
    if (through) {
      k.through++;
      // the nation it was aimed at was out before it landed: no one's land any more
      const out = k.owner > 0 ? this.out.get(k.owner) : undefined;
      if (out !== undefined && out <= this.view.tick) k.wasted++;
      // seen: the impact landed inside our sensors (an exact detonation there)
      const r = 2 * this.world.hexR;
      if (dets.some((d) => d.exact && (d.x - t.tx) ** 2 + (d.y - t.ty) ** 2 <= r * r)) k.seen = true;
    } else k.down++;
    if (k.through + k.down < k.ids.length) return;
    this.strikes = this.strikes.filter((x) => x !== k);
    this.emit({ type: 'strike', label: k.label, n: k.ids.length, through: k.through, seen: k.seen, owner: k.owner, wasted: k.wasted });
  }

  /** Is (x, y) on land owned by us or an ally (as far as we know)? */
  targetsFriendly(x: number, y: number): boolean {
    const h = nearestLandHex(this.world, x, y);
    return h >= 0 && this.isFriendly(this.view.owner[h]);
  }

  /** Is (x, y) on our own land? */
  targetsOwn(x: number, y: number): boolean {
    const h = nearestLandHex(this.world, x, y);
    return h >= 0 && this.view.owner[h] === this.view.me && !this.view.eliminated;
  }

  /** Would a blast at (x, y) cost us something (a city, a structure)? Open ground is no alarm. */
  hitsOwn(x: number, y: number): boolean {
    return blastStake(this.view, this.world, this.capitals, x, y, true) !== null;
  }

  /** Drop the launch hints of launches we track (a launch inside our cover is a track from its first tick). */
  private updateHints(): void {
    const v = this.view;
    const near = BALANCE.vision.hintJitter + 0.5;
    const close = (x: number, y: number, h: LaunchHint) => (x - h.x) ** 2 + (y - h.y) ** 2 <= near * near;
    for (const h of v.hints) {
      if (this.trackedHints.has(h.hintId)) continue;
      let seen = false;
      for (const t of v.tracks.values()) if (Math.abs(t.launchTick - h.tick) <= HINT_TRACK_TICKS && close(t.ox, t.oy, h)) seen = true;
      if (seen) this.trackedHints.add(h.hintId);
    }
    this.hints = v.hints.filter((h) => !this.trackedHints.has(h.hintId));
    if (this.trackedHints.size > 256) for (const id of this.trackedHints) if (!v.hints.some((h) => h.hintId === id)) this.trackedHints.delete(id);
  }

  /** One of our braced cities inside the level-costing rings (0–1) of a blast at (x, y). */
  private bracedCityNear(x: number, y: number): EntityView | undefined {
    const r2 = blastRingMu(this.world, 1) ** 2;
    for (const e of this.view.entities.values()) {
      if (e.kind === 'city' && e.owner === this.view.me && e.state === 'braced' && (e.x - x) ** 2 + (e.y - y) ** 2 <= r2) return e;
    }
    return undefined;
  }

  /** Name of the match-start city at (x, y), if any. */
  cityNameAt(x: number, y: number): string | null {
    return this.names.get(nearestLandHex(this.world, x, y)) ?? null;
  }

  private ownSnapshot(): Map<EntityId, OwnRow> {
    const out = new Map<EntityId, OwnRow>();
    for (const e of this.view.entities.values()) {
      if (e.owner !== this.view.me || e.state === 'building') continue;
      const ph = e.detail?.phase;
      const flying = ph === 'outbound' || ph === 'onStation' || ph === 'intercept' || ph === 'returning';
      out.set(e.id, { kind: e.kind, level: e.kind === 'city' ? (e.detail?.level ?? e.extra) : 0, x: e.x, y: e.y, flying, intercepting: ph === 'intercept' });
    }
    return out;
  }

  /** Where each aircraft in view is now (kept a few seconds after it is gone: its shot-down burst). */
  private noteAircraft(): void {
    const v = this.view;
    const p = { x: 0, y: 0 };
    for (const e of v.entities.values()) {
      if (e.kind !== 'fighter' && e.kind !== 'bomber') continue;
      segPos({ x: e.x, y: e.y, vx: e.vx, vy: e.vy, tick: e.segTick }, v.tick, p);
      this.air.set(e.id, { x: p.x, y: p.y, kind: e.kind, owner: e.owner, tick: v.tick });
    }
    for (const [id, a] of this.air) if (v.tick - a.tick > AIR_MEMORY_TICKS) this.air.delete(id);
  }

  /** An aircraft's last known spot (alive or just shot down), or null. */
  airAt(id: EntityId): { x: number; y: number; kind: 'fighter' | 'bomber'; owner: PlayerId } | null {
    return this.air.get(id) ?? null;
  }

  /**
   * What fired a shot, in words, and whose: a shooter we see (or remember) by its kind; one we
   * cannot see by the shot (a fighter's missile resolves at once, a SAM's interceptor flies), no owner.
   */
  private shooterOf(i: NetSnapshot['intercepts'][number]): { what: ShooterWord; owner: PlayerId } {
    const e = i.shooterId ? (this.view.entities.get(i.shooterId) ?? this.view.ghosts.get(i.shooterId)) : undefined;
    const kind = e?.kind ?? (i.resolveTick <= i.fireTick ? 'fighter' : 'sam');
    return { what: kind === 'fighter' ? 'FIGHTER' : kind === 'carrier' ? 'CARRIER SAM' : 'SAM', owner: e?.owner ?? 0 };
  }

  /** "SILO LOST" / "PARIS −1 LEVEL" style consequence events. */
  private emitLosses(prev: Map<EntityId, OwnRow>, s: NetSnapshot): void {
    // being knocked out takes everything at once: that moment has its own screen
    if (this.view.eliminated) return;
    for (const r of s.removes) {
      const e = prev.get(r.id);
      if (!e) continue;
      // aircraft count when shot down in the air (by a SAM: `intercepted`; ones on the ground go with their base's own line)
      const air = e.kind === 'fighter' || e.kind === 'bomber';
      if (air ? !e.flying || (r.reason !== 'destroyed' && r.reason !== 'intercepted') : r.reason !== 'destroyed') continue;
      // an aircraft shot down: by what (and whose, when we saw the shooter)
      const shot = air ? s.intercepts.find((i) => i.targetId === r.id && i.hit) : undefined;
      this.emit({ type: 'lost', kind: e.kind, name: e.kind === 'city' ? this.cityNameAt(e.x, e.y) : null, ...(shot ? { by: this.shooterOf(shot) } : {}) });
    }
    // and our side shooting theirs down
    for (const i of s.intercepts) {
      if (!i.hit || !i.shooterId) continue;
      const shooter = this.view.entities.get(i.shooterId);
      const victim = this.air.get(i.targetId);
      if (shooter?.owner !== this.view.me || !victim || victim.owner === this.view.me) continue;
      this.emit({ type: 'airKill', kind: victim.kind, owner: victim.owner, by: this.shooterOf(i).what });
    }
    let scrambled = 0;
    for (const u of s.upserts) {
      const e = prev.get(u.id);
      if (!e) continue;
      if (e.kind === 'fighter' && !e.intercepting && u.detail?.phase === 'intercept') scrambled++;
      if (e.kind !== 'city') continue;
      const level = u.detail?.level ?? u.extra;
      if (level < e.level) this.emit({ type: 'cityHit', name: this.cityNameAt(e.x, e.y), levels: e.level - level });
    }
    if (scrambled) this.emit({ type: 'scramble', fighters: scrambled });
  }

  /**
   * The focused front's caption anchor: the middle of the land being pushed (foreign hexes of
   * the focus zone that touch ours), nudged outward from our side. Null without a focus.
   */
  private computeFrontAnchor(): GameStore['frontAnchor'] {
    const v = this.view;
    const w = this.world;
    if (v.focus === null || v.eliminated) return null;
    let fx = 0;
    let fy = 0;
    let fn = 0;
    let ox = 0;
    let oy = 0;
    let on = 0;
    for (let h = 0; h < w.landCount; h++) {
      if (!this.focusZone[h]) continue;
      if (v.owner[h] === v.me) {
        ox += w.cx[h];
        oy += w.cy[h];
        on++;
        continue;
      }
      let touches = false;
      for (let k = 0; k < 6 && !touches; k++) {
        const n = w.nbr[h * 6 + k];
        touches = n >= 0 && v.owner[n] === v.me;
      }
      if (!touches) continue;
      fx += w.cx[h];
      fy += w.cy[h];
      fn++;
    }
    if (!fn) return { x: w.cx[v.focus], y: w.cy[v.focus], dx: 0, dy: 0 };
    fx /= fn;
    fy /= fn;
    if (!on) return { x: fx, y: fy, dx: 0, dy: 0 };
    const d = Math.hypot(fx - ox / on, fy - oy / on) || 1;
    const dx = (fx - ox / on) / d;
    const dy = (fy - oy / on) / d;
    return { x: fx + dx * w.hexR, y: fy + dy * w.hexR, dx, dy };
  }

  private recomputeFocus(hex: number | null): void {
    this.focusHex = hex;
    this.focusZone.fill(0);
    if (hex === null) return;
    const w = this.world;
    const q0 = w.q[hex];
    const r0 = w.r[hex];
    for (let h = 0; h < w.landCount; h++) if (axialDist(w.q[h], w.r[h], q0, r0) <= FOCUS_RADIUS) this.focusZone[h] = 1;
  }

  private rebuildHexState(): void {
    const v = this.view;
    const st = this.hexState;
    for (let h = 0; h < this.world.landCount; h++) {
      const o = h * 4;
      st[o] = v.owner[h];
      st[o + 1] = v.dens[h];
      st[o + 2] = v.rad[h];
      // start cores are public (and with public borders every owner): an owner we were told about counts as knowledge
      const seen = BALANCE.vision.publicBorders || v.lastSeenTick[h] >= 0 || v.owner[h] !== 0;
      const live = v.live[h] || (this.revealed && seen);
      // how long ago we last saw it (stale land fades with age; never seen = fully faded)
      const stale = live ? 0 : v.lastSeenTick[h] < 0 ? HEX_STALE_MAX : Math.min(HEX_STALE_MAX, Math.floor((v.tick - v.lastSeenTick[h]) / STALE_STEP_TICKS));
      st[o + 3] = (live ? HEX_LIVE : 0) | (seen ? HEX_SEEN : 0) | (this.focusZone[h] ? HEX_FOCUS : 0) | (stale << HEX_STALE_SHIFT);
    }
    this.hexDirty = true;
  }
}
